import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createViewDealCreate } from "@/modules/rev_apac/partners/views/ViewDealCreate";
import { routerPaths } from "@/modules/rev_apac/router";
import { NOOP } from "@/utils/utils";
import type { TApiDeal } from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import type { TAsyncState } from "@/store";
import { buildDealCopyPrefill } from "@/ui/organisms/DealWizard";
import type { TDealCopyPrefill } from "@/ui/organisms/DealWizard";
import { getNow } from "@/utils/dateService";
import { fetchAllPages } from "./wireDealDetail";
import { buildPropcodeMappingFile } from "./propcodeMapping/buildPropcodeMappingFile";

export function wireDeals({ useAppStore, api }: TCompositionDeps) {
    const ViewDealCreate = createViewDealCreate({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const navigate = useNavigate();

            const [preselectedServiceProviderId, setPreselectedServiceProviderId] = useState<
                string | null
            >(null);

            const [serviceProviders, setServiceProviders] = useState<{
                data: Awaited<ReturnType<typeof api.serviceProviders.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [portfolioCompanies, setPortfolioCompanies] = useState<{
                data: Array<{
                    id: string;
                    name: string;
                    code: string;
                }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [users, setUsers] = useState<{
                data: Awaited<ReturnType<typeof api.users.list>>["items"] | null;
                loading: boolean;
            }>({ data: null, loading: true });

            const [copySource, setCopySource] = useState<TAsyncState<TDealCopyPrefill>>({
                status: "idle",
                data: null,
                error: null,
            });

            // Read everything the wizard needs from the deal being copied. Nothing is
            // written: the copy is only created when the wizard is submitted.
            const loadCopySource = useCallback(async (dealId: string) => {
                setCopySource({ status: "pending", data: null, error: null });
                try {
                    const [deal, columnMappings, propcodeMappings, otbInfo] = await Promise.all([
                        api.deals.get(dealId),
                        fetchAllPages((skip, limit) =>
                            api.deals.getColumnMappings(dealId, { skip, limit }),
                        ),
                        fetchAllPages((skip, limit) =>
                            api.deals.getPropcodeMappings(dealId, { skip, limit }),
                        ),
                        api.deals.getOTBInfo(dealId).catch(() => null),
                    ]);

                    let otbFile: File | null = null;
                    let otbUnavailableReason: string | null = null;
                    if (otbInfo?.has_otb && otbInfo.file_id) {
                        try {
                            otbFile = await api.files.fetchOriginal(otbInfo.file_id);
                        } catch {
                            otbUnavailableReason =
                                "The OTB file of the copied deal could not be loaded. Upload an OTB file or use a placeholder.";
                        }
                    } else if (otbInfo === null) {
                        otbUnavailableReason =
                            "The OTB of the copied deal could not be loaded. Upload an OTB file or use a placeholder.";
                    }

                    // The shared prefill writes a one-sheet propcode file keyed by level.
                    // APAC mappings are keyed by kind, so the file is rebuilt as the
                    // three-sheet workbook and the wizard takes its rows from validating it.
                    const prefill = buildDealCopyPrefill(
                        {
                            deal,
                            columnMappings,
                            propcodeMappings,
                            otbFile,
                            otbUnavailableReason,
                        },
                        getNow(),
                    );
                    setCopySource({
                        status: "fulfilled",
                        data: {
                            ...prefill,
                            propcodeFile: buildPropcodeMappingFile(deal.name, propcodeMappings),
                            propcodeRows: [],
                        },
                        error: null,
                    });
                } catch (err) {
                    setCopySource({
                        status: "rejected",
                        data: null,
                        error:
                            err instanceof Error
                                ? err
                                : new Error("Failed to load the deal to copy"),
                    });
                }
            }, []);

            const loadAll = useCallback(
                async (bpId?: string) => {
                    // Store preselected service provider ID
                    if (bpId) {
                        setPreselectedServiceProviderId(bpId);
                    }

                    setServiceProviders({ data: null, loading: true });
                    try {
                        const spData = await api.serviceProviders.list({
                            skip: 0,
                            limit: 1000,
                            business_partner_type: "service_provider",
                        });
                        setServiceProviders({ data: spData.items, loading: false });
                    } catch {
                        setServiceProviders({ data: [], loading: false });
                    }

                    setPortfolioCompanies({ data: null, loading: true });
                    try {
                        const pcData = await api.portfolioCompanies.list({
                            skip: 0,
                            limit: 100,
                        });
                        setPortfolioCompanies({
                            data: pcData.items.map((pc) => ({
                                id: pc.id,
                                name: pc.name,
                                code: pc.code,
                            })),
                            loading: false,
                        });
                    } catch {
                        setPortfolioCompanies({ data: [], loading: false });
                    }

                    setUsers({ data: null, loading: true });
                    try {
                        const userData = await api.users.list({
                            page_size: 1000,
                            finance_team: true,
                        });
                        setUsers({ data: userData.items, loading: false });
                    } catch {
                        setUsers({ data: [], loading: false });
                    }
                },
                [effectiveCompanyId],
            );

            return {
                onMount: loadAll,
                onUnmount: NOOP,
                useServiceProviders: () => serviceProviders,
                usePortfolioCompanies: () => portfolioCompanies,
                useUsers: () => users,
                usePreselectedServiceProviderId: () => preselectedServiceProviderId,
                onCreateDeal: useCallback(async (data: Parameters<typeof api.deals.create>[0]) => {
                    const createdDeal = await api.deals.create(data);
                    return createdDeal;
                }, []),
                onCheckDealNameTaken: useCallback(
                    async (
                        name: string,
                        partner: { serviceProviderId?: string; portfolioCompanyId?: string },
                    ) => {
                        const wanted = name.trim().toLowerCase();
                        if (!wanted) return false;
                        // `name` is a contains-filter and the list includes inactive deals,
                        // which also hold their name; compare exactly, ignoring case, as the
                        // unique index does.
                        const page = await api.deals.list({
                            name: name.trim(),
                            service_provider_id: partner.serviceProviderId,
                            portfolio_company_id: partner.portfolioCompanyId,
                            limit: 100,
                        });
                        return page.items.some((d) => d.name.trim().toLowerCase() === wanted);
                    },
                    [],
                ),
                useCopySource: () => copySource,
                onLoadCopySource: loadCopySource,
                onDealCreateSuccess: useCallback(
                    (createdDeal?: TApiDeal, partnerId?: string) => {
                        // A copy lands on the new deal so the carried-over setup can be reviewed.
                        if (createdDeal && partnerId) {
                            navigate(
                                routerPaths.admin.businessPartnerDealDetail(
                                    partnerId,
                                    createdDeal.id,
                                ),
                            );
                            return;
                        }
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
                onExtractColumns: useCallback(async (file: File, headerRowNumber: number) => {
                    return api.deals.extractColumns(file, headerRowNumber);
                }, []),
                onValidatePropcodeMappingFile: useCallback(async (file: File) => {
                    return api.deals.validatePropcodeMappingFile(file);
                }, []),
                onValidateOTB: useCallback(
                    async (
                        file: File,
                        columnMappings: Array<{
                            file_type: string;
                            field_type: string;
                            column_name: string;
                            header_row_number: number;
                        }>,
                        startPeriod: string,
                        filteringRules?: Array<{
                            filtering_column_header: string;
                            filtering_value: string[];
                            include_filtered_values: boolean;
                        }>,
                    ) => {
                        return api.deals.validateOTB(
                            file,
                            columnMappings,
                            startPeriod,
                            filteringRules,
                        );
                    },
                    [],
                ),
                onUploadOTB: useCallback(
                    async (
                        dealId: string,
                        file: File,
                        options?: { sheetName?: string; dateFormat?: string },
                    ) => {
                        return api.deals.uploadOTB(dealId, file, options);
                    },
                    [],
                ),
                onGeneratePlaceholderOTB: useCallback(
                    async (dealId: string, propertyCode: string) => {
                        return api.deals.generatePlaceholderOTB(dealId, propertyCode);
                    },
                    [],
                ),
                onCancel: useCallback(
                    (_bpId?: string) => {
                        navigate(routerPaths.admin.businessPartners);
                    },
                    [navigate],
                ),
            };
        },
    });

    return { ViewDealCreate };
}
